/**
 * DeleteAccountPage (T27.2.10.2) — the PUBLIC Data Safety "Delete account URL".
 *
 * Google Play's Data Safety → account-creation flow requires a public web page
 * that (1) names the app/developer, (2) prominently features the STEPS to request
 * deletion, and (3) states what data is DELETED vs KEPT plus any retention period.
 * This page leads with exactly those three blocks, mounted OUTSIDE AuthGuard so a
 * logged-out reviewer can deep-link it.
 *
 * The deletion steps describe the real in-app paths that exist today:
 *   - "Leave household" (Settings → Account & members) — removes you from a
 *     household; blocked when it is your ONLY household (backend guard).
 *   - "Delete household" (manager-only danger zone) — permanently deletes the
 *     household and its data.
 *   - For anyone who cannot self-serve (your only household, or a member who is
 *     not the manager), the request fallback is an email to the published privacy
 *     contact (CONTACT_EMAIL), mirroring privacy-policy.md § Contact Us.
 *
 * The data-deleted-vs-kept + retention detail is owned by the Privacy Policy's
 * Data Retention section; this page summarizes and CROSS-LINKS it (per AC) rather
 * than duplicating the table.
 */

import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { routes } from '../../routes/paths';
import { LegalPageShell } from './LegalDocPage';
import { CONTACT_EMAIL, LEGAL_ENTITY } from './content';

const titleStyle: CSSProperties = {
  // T47 Phase 4 — page title + section headings → Poppins display.
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-xl)',
  fontWeight: 'var(--font-weight-bold)',
  lineHeight: 'var(--line-height-tight)',
  margin: 0,
};

const sectionHeadingStyle: CSSProperties = {
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-semibold)',
  margin: 0,
  marginTop: 'var(--space-6)',
  marginBottom: 'var(--space-2)',
};

const paraStyle: CSSProperties = {
  margin: 0,
  marginBottom: 'var(--space-3)',
  lineHeight: 'var(--line-height-relaxed)',
  color: 'var(--color-text-primary)',
};

const leadStyle: CSSProperties = {
  ...paraStyle,
  color: 'var(--color-text-secondary)',
};

const listStyle: CSSProperties = {
  margin: 0,
  marginBottom: 'var(--space-3)',
  paddingLeft: 'var(--space-6)',
  lineHeight: 'var(--line-height-relaxed)',
  color: 'var(--color-text-primary)',
};

const stepListStyle: CSSProperties = {
  ...listStyle,
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
};

const liStyle: CSSProperties = {
  marginBottom: 'var(--space-1)',
};

const linkStyle: CSSProperties = {
  color: 'var(--color-accent, var(--color-text-primary))',
  textDecoration: 'underline',
};

const calloutStyle: CSSProperties = {
  margin: 0,
  marginTop: 'var(--space-2)',
  marginBottom: 'var(--space-3)',
  padding: 'var(--space-3) var(--space-4)',
  backgroundColor: 'var(--color-bg-muted)',
  borderRadius: 'var(--radius-md)',
  borderLeftWidth: 'var(--border-width-hairline)',
  borderLeftStyle: 'solid',
  borderLeftColor: 'var(--color-border-strong)',
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--font-size-sm)',
  lineHeight: 'var(--line-height-relaxed)',
};

export function DeleteAccountPage(): JSX.Element {
  return (
    <LegalPageShell testId="legal-delete-account">
      <h1 style={titleStyle}>Delete your account</h1>

      {/* Block 1 — app / developer name (Play requirement). The publishing
          entity is the finalized value, mirroring privacy-policy.md § Who We Are. */}
      <p style={leadStyle} data-testid="delete-account-app">
        This page explains how to delete your <strong>Shopista</strong> account
        and household data. Shopista is operated by{' '}
        <strong>{LEGAL_ENTITY}</strong>.
      </p>

      {/* Block 2 — the steps to request deletion (Play requirement). These match
          the real in-app paths: Leave household / Delete household, plus the
          email request fallback for anyone who cannot self-serve. */}
      <h2 style={sectionHeadingStyle} data-testid="delete-account-steps-heading">
        How to delete your data
      </h2>
      <p style={paraStyle}>
        Open <strong>Settings → Account &amp; members</strong> in the app, then:
      </p>
      <ol style={stepListStyle} data-testid="delete-account-steps">
        <li style={liStyle}>
          <strong>Leave a household</strong> — tap <strong>Leave household</strong>{' '}
          to remove yourself from it. (If it is your <em>only</em> household, the
          app blocks this so you are never left without one — use the email
          request below instead.)
        </li>
        <li style={liStyle}>
          <strong>Delete a household</strong> — a household manager can tap{' '}
          <strong>Delete household</strong> in the danger zone to permanently
          delete the household and everything in it.
        </li>
        <li style={liStyle}>
          <strong>Request full deletion by email</strong> — if you cannot
          self-serve (your only household, or you are a member rather than the
          manager), email us at <strong>{CONTACT_EMAIL}</strong> and we
          will delete your account and associated personal data.
        </li>
      </ol>
      <p style={calloutStyle} data-testid="delete-account-contact">
        Deletion request contact: <strong>{CONTACT_EMAIL}</strong>. We
        respond within the timeframe required by applicable law (generally 30 days
        under Canada&apos;s PIPEDA).
      </p>

      {/* Block 3 — what is deleted vs kept + retention (Play requirement),
          cross-linking the Privacy Policy's Data Retention section rather than
          duplicating its table. */}
      <h2 style={sectionHeadingStyle} data-testid="delete-account-data-heading">
        What is deleted, what is kept, and for how long
      </h2>
      <p style={paraStyle}>When your account is deleted:</p>
      <ul style={listStyle} data-testid="delete-account-deleted">
        <li style={liStyle}>
          <strong>Deleted or anonymized:</strong> your account and identity
          (email, display name, profile photo), and your household data —
          shopping lists, products, preferences, selected stores, and member
          details — subject to backups.
        </li>
        <li style={liStyle}>
          <strong>Sessions:</strong> your sign-in sessions and refresh tokens are
          removed when they expire or you sign out.
        </li>
      </ul>
      <p style={paraStyle} data-testid="delete-account-kept">
        <strong>Kept for a limited period:</strong> some information persists
        briefly after deletion — encrypted backups age out on their normal cycle,
        and diagnostic / crash reports held by our error-monitoring provider
        (Sentry) expire on that provider&apos;s retention window (typically up to
        90 days). We keep nothing longer than needed to operate and secure the
        service or to meet a legal obligation.
      </p>
      <p style={paraStyle} data-testid="delete-account-retention">
        For the full, itemized retention schedule, see the{' '}
        <Link to={routes.privacy} style={linkStyle} data-testid="delete-account-privacy-link">
          Privacy Policy — Data Retention
        </Link>{' '}
        section.
      </p>
    </LegalPageShell>
  );
}
